import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { useCalls, useIncidents, usePortalClient } from '@ngsos/portal-core/portal';

import { AgencyView } from '@/app/shell/AgencyConfigurationProvider';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { LoadingFailedBanner, LoadingFailedBox, NgButton, useDebugMode } from '@/ui';
import { ATE_AGENCY_ID } from '@/features/shared/knownAgencyIds';
import { selectIncidentMapPoints, toRowFromIncident } from '@ngsos/portal-core/incidents';
import { parseCreateIncidentDialogModel } from '@/features/create-incident/createIncidentRedirect';
import type { IncidentListContext } from './incidentListContext';
import { IncidentListLayoutBox } from './IncidentListLayoutBox';
import { IncidentListContent } from './IncidentListContent';
import { IncidentListMapContent } from './IncidentListMapContent';
import { NewIncidentDialog } from './NewIncidentDialog';
import type { NewIncidentDialogHandle } from './NewIncidentDialog';
import { NewIncidentFromDataSentenceDialog } from './NewIncidentFromDataSentenceDialog';
import type { NewIncidentFromDataSentenceDialogHandle } from './NewIncidentFromDataSentenceDialog';

export function IncidentListPage() {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const [searchParams] = useSearchParams();

  const defaultCountryCode = (agencyConfiguration.agencyPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const [context, setContext] = useState<IncidentListContext>('incidents');

  const newIncidentDialogRef = useRef<NewIncidentDialogHandle>(null);
  const newIncidentFromDataSentenceDialogRef = useRef<NewIncidentFromDataSentenceDialogHandle>(null);

  const portalClient = usePortalClient();
  const debugModeOn = useDebugMode();
  const { liveIncidents, dataState } = useIncidents();
  const { calls: callNotifications } = useCalls();
  const incidents = debugModeOn ? liveIncidents : liveIncidents.filter((incident) => incident.purpose !== 'Test');

  const isLoading = dataState === 'loading';
  const dataLoadFailed = dataState === 'failed';
  const hasLoadedData = incidents.length > 0 || callNotifications.length > 0;

  const retryDataLoad = async () => {
    await portalClient?.reload();
  };

  const createIncidentModel = useMemo(() => parseCreateIncidentDialogModel(searchParams), [searchParams]);
  const dialogOpenedFromQueryRef = useRef(false);

  useEffect(() => {
    if (isLoading || dialogOpenedFromQueryRef.current || createIncidentModel.requestedIncidentId == null) {
      return;
    }

    dialogOpenedFromQueryRef.current = true;

    let phoneNumber = createIncidentModel.phoneNumber.trim();
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = `${defaultCountryCode}${phoneNumber}`;
    }

    newIncidentDialogRef.current?.showModal({
      requestedIncidentId: createIncidentModel.requestedIncidentId,
      phoneNumber,
      callerName: createIncidentModel.callerName,
      label: createIncidentModel.label,
      summary: createIncidentModel.summary,
      location: createIncidentModel.location,
    });
  }, [isLoading, createIncidentModel, defaultCountryCode]);

  const createNewIncident = () => {
    newIncidentDialogRef.current?.showModal();
  };

  const showNewIncidentFromDataSentenceModal = () => {
    newIncidentFromDataSentenceDialogRef.current?.showModal();
  };

  const incidentMapPoints = selectIncidentMapPoints(incidents)?.map(toRowFromIncident);

  return (
    <>
      <IncidentListLayoutBox
        bannersArea={dataLoadFailed && hasLoadedData ? <LoadingFailedBanner onRetry={retryDataLoad} /> : null}
        incidentListArea={
          dataLoadFailed && !hasLoadedData ? (
            <LoadingFailedBox onRetry={retryDataLoad} />
          ) : (
            <IncidentListContent
              isLoading={isLoading}
              onTabSwitch={setContext}
              incidents={incidents}
              callNotifications={callNotifications}
            />
          )
        }
        actionBarArea={
          <div className="d-flex flex-column flex-tablet-row gap-2 align-items-stretch">
            <NgButton
              text={t('Button_NewIncident')}
              icon="fal fa-plus-circle"
              testID="incident-list-new-incident-button"
              onClick={createNewIncident}
            />

            <AgencyView agencyId={ATE_AGENCY_ID}>
              <NgButton
                text={t('Button_NewIncidentFromDataSentence')}
                icon="fal fa-plus-circle"
                onClick={showNewIncidentFromDataSentenceModal}
              />
            </AgencyView>
          </div>
        }
        mapArea={
          <IncidentListMapContent
            context={context}
            incidents={incidentMapPoints}
            sourceIncidents={incidents}
            callNotifications={callNotifications}
          />
        }
      />

      <NewIncidentDialog ref={newIncidentDialogRef} />
      <NewIncidentFromDataSentenceDialog ref={newIncidentFromDataSentenceDialogRef} />
    </>
  );
}
